import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const Input = z.object({
  messages: z
    .array(z.object({ role: z.enum(["user", "assistant"]), text: z.string().trim().min(1).max(600) }))
    .min(1)
    .max(30),
  /** Τι βρήκε το ευρετήριο του καταστήματος για το τελευταίο μήνυμα (μάρκες/τίτλοι) — για να αναγνωρίζονται μάρκες όπως «Overlay». */
  catalog: z
    .object({
      brands: z.array(z.string().max(80)).max(8),
      titles: z.array(z.string().max(160)).max(8),
    })
    .optional(),
});

export type PandakiReply = {
  reply: string;
  options: string[];
  searchQuery: string;
};

/** Pandaki: συνομιλία αγορών. Επιστρέφει απάντηση, έτοιμες επιλογές και φράση αναζήτησης
 *  που ο browser τρέχει στο υπάρχον ευρετήριο — ποτέ επινοημένα προϊόντα. */
export const pandakiChat = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Input.parse(d))
  .handler(async ({ data }): Promise<{ result: PandakiReply | null; error?: string }> => {
    const { getRequest } = await import("@tanstack/react-start/server");
    const { getClientIp, checkRateLimit } = await import("./abuse-guard.server");
    const ip = getClientIp(getRequest());
    if (!(await checkRateLimit("pandaki-chat", ip, { max: 80, windowMinutes: 10 })).allowed)
      return { result: null, error: "rate" };
    const { aiJson, AiSearchBlocked } = await import("./ai-search.server");
    let transcript = data.messages
      .map((m) => `${m.role === "user" ? "Πελάτης" : "Pandaki"}: ${m.text}`)
      .join("\n");
    if (data.catalog && (data.catalog.brands.length || data.catalog.titles.length)) {
      transcript +=
        "\n\n[Ευρετήριο καταστήματος για το τελευταίο μήνυμα του πελάτη]" +
        (data.catalog.brands.length ? `\nΜάρκες που ταιριάζουν: ${data.catalog.brands.join(", ")}` : "") +
        (data.catalog.titles.length ? `\nΠροϊόντα που βρέθηκαν: ${data.catalog.titles.join(" | ")}` : "");
    }
    try {
      const r = await aiJson<PandakiReply>(
        "Είσαι το Pandaki 🐼, ο παιχνιδιάρης βοηθός αγορών του ελληνικού eshop MrPanda (σούπερ μάρκετ, σνακ, ποτά, καλλυντικά, είδη σπιτιού, παιχνίδια). " +
          "Μιλάς ελληνικά, ζεστά, σύντομα, με 1 emoji το πολύ. Βοηθάς τον πελάτη να βρει τι θέλει κάνοντας ΜΙΑ στοχευμένη ερώτηση τη φορά " +
          "(π.χ. για σοκολάτα: γάλακτος, μαύρη, χωρίς ζάχαρη, με φουντούκια). " +
          "Πεδία: reply = έως 200 χαρακτήρες. options = 2-6 σύντομες απαντήσεις (έως 28 χαρακτήρες) που ο πελάτης μπορεί να πατήσει, σχετικές με την ερώτησή σου. " +
          "searchQuery = 1-3 απλές ελληνικές λέξεις όπως γράφονται σε τίτλο προϊόντος σούπερ μάρκετ (π.χ. «σοκολάτα γάλακτος», «απορρυπαντικό βρεφικό»), χωρίς περιττές λέξεις όπως «ρούχων», ή κενό αν δεν έχει ζητήσει ακόμη προϊόν. " +
          "ΜΑΡΚΕΣ: αν ο πελάτης γράψει όνομα μάρκας ή προϊόντος (π.χ. Overlay, Fairy, Ariel, Lacta), ΜΗΝ ρωτάς τι εννοεί· είναι μάρκα. Κράτα το όνομα της μάρκας ΑΥΤΟΥΣΙΟ (με λατινικούς χαρακτήρες όπως το έγραψε) μέσα στο searchQuery μαζί με το είδος (π.χ. «Overlay λίπη»), " +
          "πες ότι του δείχνεις τα προϊόντα της μάρκας δίπλα και, αν θες, ρώτα μια διευκρίνιση για να στενέψει (π.χ. σπρέι ή ανταλλακτικό). " +
          "Αν σου δοθεί «[Ευρετήριο καταστήματος]», αυτά υπάρχουν πραγματικά στο κατάστημα: χρησιμοποίησέ τα για να καταλάβεις τι ζητά και κράτα στο searchQuery λέξεις που υπάρχουν σε αυτούς τους τίτλους. " +
          "Αν αλλάξει θέμα, η αναζήτηση αφορά το νέο προϊόν. Μην επινοείς τιμές, προσφορές ή συγκεκριμένα προϊόντα· τα προϊόντα εμφανίζονται δίπλα αυτόματα.",

        transcript,
        {
          type: "object",
          additionalProperties: false,
          required: ["reply", "options", "searchQuery"],
          properties: {
            reply: { type: "string" },
            options: { type: "array", items: { type: "string" } },
            searchQuery: { type: "string" },
          },
        },
      );
      if (!r) return { result: null, error: "unavailable" };
      return {
        result: {
          reply: r.reply.slice(0, 300),
          options: r.options.map((o) => o.slice(0, 40)).filter(Boolean).slice(0, 6),
          searchQuery: r.searchQuery.slice(0, 60).trim(),
        },
      };
    } catch (e) {
      if (e instanceof AiSearchBlocked) return { result: null, error: "blocked" };
      console.error("pandaki", e);
      return { result: null, error: "unavailable" };
    }
  });
